import { useEffect, useMemo, useRef, useState } from "react";
import { readIsPhone, useIsPhone } from "../lib/useIsPhone";
import { Map as MLMap, NavigationControl, Popup, LngLatBounds } from "maplibre-gl";
import { FOOTPRINT_LABELS, footprintReasonTag, footprintDeclaredText, footprintRelayedText, scopeStaleLabel, type AuditFootprint } from "../lib/auditTable";
import type { GeoJSONSource, MapGeoJSONFeature } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import type { Feature as GJFeature, FeatureCollection as GJFeatureCollection, LineString as GJLineString, Point as GJPoint } from "geojson";
import { tryApiGet } from "../api/client";
import { buildBackboneMapStyle, MAP_CONTAINER_BACKGROUND } from "../lib/mapStyle";
import { relativeTime } from "../lib/format";
import { formatDistanceBoth, type DistanceUnitPref } from "../lib/units";
import { useAppData } from "../state/AppData";
import { backboneCredibilityValue, backboneReasonLine, backboneTrafficLine, backboneTrafficTooltip } from "../lib/topologyText";
import { HoverTip } from "./HoverTip";
import type { BackboneLink, BackboneNode, BackboneResponse } from "../api/types";
import { AuditWindowControls, useAuditAsOf, useAuditHistory } from "./AuditHistory";
import { historyQuery } from "../lib/auditHistory";
import { BACKBONE_CLASS_INFO, BACKBONE_DEFAULT_WINDOW, BACKBONE_DETAILS_EXIT_LABEL, BACKBONE_DETAILS_TITLE, BACKBONE_DOT_STROKE_COLOR, BACKBONE_EMPTY, BACKBONE_LEGEND_CLOSE_LABEL, BACKBONE_LEGEND_DIRECTION_NOTE, BACKBONE_LEGEND_HEADING, BACKBONE_LEGEND_LINKS_HEADING, BACKBONE_LEGEND_PANEL_TITLE, BACKBONE_LEGEND_TOGGLE_FAINT, BACKBONE_LEGEND_TOGGLE_NEITHER, BACKBONE_LINK_KIND_INFO, BACKBONE_LOADING, BACKBONE_PENDING, BACKBONE_METRIC_BETWEENNESS, BACKBONE_METRIC_BETWEENNESS_CAPTION, BACKBONE_METRIC_CREDIBILITY, BACKBONE_METRIC_CREDIBILITY_CAPTION, BACKBONE_METRIC_LINK_SYMMETRY, BACKBONE_METRIC_LINK_SYMMETRY_CAPTION, BACKBONE_METRIC_REACHABILITY, BACKBONE_METRIC_REACHABILITY_CAPTION, BACKBONE_METRIC_REACHABILITY_LIMITED, BACKBONE_METRIC_REPEATABILITY, BACKBONE_METRIC_REPEATABILITY_CAPTION, BACKBONE_METRIC_STABILITY, BACKBONE_METRIC_STABILITY_CAPTION, BACKBONE_NEIGHBOURS_NONE, BACKBONE_PILL_FILL_COLOR, BACKBONE_PILL_TEXT_COLOR, BACKBONE_REASON_INFERRED_SUFFIX, BACKBONE_REGION_NOT_VERIFIED, BACKBONE_SCOPES_NONE, BACKBONE_SECTION_ALSO_JUDGED, BACKBONE_SECTION_ALSO_JUDGED_DESC, BACKBONE_SECTION_LAST_HEARD, BACKBONE_SECTION_METRICS, BACKBONE_SECTION_NEIGHBOURS, BACKBONE_NEIGHBOURS_SHOW_ALL, BACKBONE_SECTION_REGION, BACKBONE_SECTION_SCOPES, BACKBONE_SECTION_FOOTPRINT, BACKBONE_SECTION_TRAFFIC, BACKBONE_SECTION_WHY, BACKBONE_SECTION_WHY_NOT, BACKBONE_REGION_TIP, BACKBONE_SCOPES_TIP, BACKBONE_WHY_NOT_PER_UNIT, BACKBONE_SOURCE_CHIP_LABEL, BACKBONE_TRAFFIC_NOT_RANKED, BACKBONE_UNAVAILABLE, NEIGHBOUR_LINK_COLOR, TOPOLOGY_NEIGHBOUR_LINKS_LABEL, TOPOLOGY_NEIGHBOUR_LINKS_TIP, backboneBetweennessValue, backboneCompactCountsLine, backboneClassPills, backboneDistanceLabel, backboneLinkDirectionLabel, backboneLinkSummary, backboneNeighbourMeta, backboneNeighbourSymmetryLabel, backboneNodeClass, backboneReachabilityValue, backboneRegionSourceTitle, backboneScopeSourceTitle, backboneStaleWarning, type BackboneClass, type BackboneSourceKind } from '../lib/definitions/topology';
import { type AuditWindow } from '../lib/definitions/analysis';

// Backbone tab (2026-09-30) — ported from the approved prototype
// (amalgamesh-db-snapshots/screenshots-2026-09-29/backbone-maps/
// backbone-mm7.html, plus the review-round-4 fixes) into this app's own
// MapLibre/Audit conventions. Key structural choices carried over
// verbatim from the prototype, for the reasons documented there:
//   - NO clustering, ever: every repeater (classified or not) is its own
//     GL marker, so a stable link's endpoint can never go dangling when a
//     cluster splits/merges across zoom levels.
//   - A bigger, class-coloured CIRCLE sits under every classified
//     repeater's name-pill. Circle layers have no collision system in
//     MapLibre, so this dot can never be dropped by label collision —
//     only the pill on top of it can hide, never the colour itself.
//   - Below zoom 7, only "both"-class repeaters and the top-2-by-traffic
//     classified repeaters per region keep a name pill at every zoom;
//     every other classified repeater is a plain coloured dot until zoom
//     7 (hover always shows its name regardless of zoom).
//   - No rule dropdown: the coordinator's follow-up made regional-backbone
//     classification a single decided rule that lives in Settings.

const REGION_ZOOM = 8;
const PILL_PROMINENT_MIN_ZOOM = 7;
const NEITHER_LABEL_MIN_ZOOM = 10;
// Ported from the prototype's own PLAUSIBLE bbox — the mesh this app runs
// against sits in this rough box; a repeater with a bad reported position
// (GPS 0,0 or similar) must never widen the initial camera fit to a
// world-view.
const PLAUSIBLE_BOUNDS = { lonMin: -130, lonMax: -100, latMin: 20, latMax: 55 };

// One comparison per line on purpose (never `a >= x && a <= y` chained on
// one line) — lint:copy's plain-text JSX-text scanner reads a same-line
// `>=` ... `<=` pair as a fake `>text<` tag boundary and flags the
// PLAUSIBLE_BOUNDS identifier between them as a false-positive ALL_CAPS
// user-visible string. Splitting the comparisons across lines is the
// straightforward fix, not a lint suppression.
function isPlausibleCoord(lon: number, lat: number): boolean {
  if (lon < PLAUSIBLE_BOUNDS.lonMin) return false;
  if (lon > PLAUSIBLE_BOUNDS.lonMax) return false;
  if (lat < PLAUSIBLE_BOUNDS.latMin) return false;
  if (lat > PLAUSIBLE_BOUNDS.latMax) return false;
  return true;
}

function truncateWordBoundary(name: string, max: number): string {
  if (!name || name.length <= max) return name ?? "";
  let cut = name.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  if (lastSpace > max * 0.4) cut = cut.slice(0, lastSpace);
  return `${cut.trimEnd()}…`;
}

// 2026-09-30 pill-colour task: every pill is now BLACK fill / WHITE text,
// with a thick (~3px) class-coloured outline standing in for the old
// per-class fill colour — `fill` below is always BACKBONE_PILL_FILL_COLOR
// at the call site, `stroke` is always the class's own BACKBONE_CLASS_INFO
// color. Canvas bumped from 44x26 to 46x28 and the inset from 1px to 2px
// (half of the new 3px line width, rounded up) so the thicker stroke has
// room to draw without clipping at the canvas edge.
function makePillImage(fill: string, stroke: string): { data: ImageData; w: number; h: number; r: number } {
  const w = 46;
  const h = 28;
  const r = 12;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, w, h);
  const x = 2;
  const y = 2;
  const ww = w - 4;
  const hh = h - 4;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + ww, y, x + ww, y + hh, r);
  ctx.arcTo(x + ww, y + hh, x, y + hh, r);
  ctx.arcTo(x, y + hh, x, y, r);
  ctx.arcTo(x, y, x + ww, y, r);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = stroke;
  ctx.stroke();
  return { data: ctx.getImageData(0, 0, w, h), w, h, r };
}

interface NodeProps {
  id: string;
  name: string;
  short_id: string;
  name_short22: string;
  cls: BackboneClass;
  pill_prominent: boolean;
  in_scope: boolean;
}

interface LinkProps {
  kind: "backhaul" | "backbone" | "faint";
  symmetry: string;
  direction: string | null;
  a: string;
  b: string;
  a_name: string;
  b_name: string;
  transmissions: number;
  days: number;
  distance_km: number | null;
  position_note: string | null;
  in_scope: boolean;
}

function linkKind(link: BackboneLink): "backhaul" | "backbone" | "faint" {
  if (link.is_backhaul_link) return "backhaul";
  if (link.is_backbone_link) return "backbone";
  return "faint";
}

function esc(s: string | null | undefined): string {
  const d = document.createElement("div");
  d.textContent = s ?? "";
  return d.innerHTML;
}

// 2026-09-30 fix 6: was a literal `color:#666` -- fine on the library's
// own default WHITE popup chrome, unreadable once the popup itself went
// dark-themed (global.css's .maplibregl-popup-content). Inline style
// attributes on a raw HTML string still resolve CSS custom properties
// from the page, so this tracks the theme like everything else.
function pillTooltipHtml(name: string, shortId: string): string {
  return `<div style="font-weight:700;">${esc(name)}</div><div style="font-family:monospace;color:var(--ink-secondary);">${esc(shortId)}</div>`;
}

function linkTooltipHtml(p: LinkProps, pref: DistanceUnitPref | undefined): string {
  const dir = backboneLinkDirectionLabel(p.symmetry, p.direction, p.a_name, p.b_name);
  const dist = p.distance_km === null || p.distance_km === undefined ? backboneDistanceLabel(null) : formatDistanceBoth(p.distance_km, pref, 1);
  let html = `<div style="font-weight:700;">${esc(p.a_name)} ↔ ${esc(p.b_name)}</div>`;
  html += `<div>${esc(dist)} · ${esc(backboneLinkSummary(p.transmissions, p.days))}</div>`;
  html += `<div>${esc(dir)}</div>`;
  if (p.position_note) html += `<div style="color:var(--ink-muted);font-size:10.5px;">${esc(p.position_note)}</div>`;
  return html;
}

function sourceChipTitle(kind: "region" | "scope", node: BackboneNode): { chip: BackboneSourceKind; title: string } {
  if (kind === "region") {
    const src = (node.region_provenance?.source ?? "none") as BackboneSourceKind;
    return { chip: src, title: backboneRegionSourceTitle(src, node.region_provenance?.note ?? null, node.region_provenance?.estimate_neighbour_count ?? null) };
  }
  const sp = node.scope_provenance;
  const src = (sp?.source ?? "none") as BackboneSourceKind;
  return { chip: src, title: backboneScopeSourceTitle(src, sp?.dominance_pct ?? null, sp?.days ?? null) };
}

function metricRow(label: string, value: string, title: string, caption: string) {
  return (
    <div key={label} className="backbone-metric">
      <div className="backbone-metric-row" title={title}>
        <span className="backbone-metric-label">{label}</span>
        <span className="backbone-metric-value">{value}</span>
      </div>
      <div className="settings-field-desc backbone-metric-caption">{caption}</div>
    </div>
  );
}

function isWhyNotPerUnit(key: string, hasBackhaulMiss: boolean): boolean {
  if (key === "no_backhaul_link") return hasBackhaulMiss;
  return /^regional_.*_miss$/.test(key) && key !== "regional_backbone_miss" || key === "unit_not_judged" || key === "regional_no_unit_links";
}

function DetailsPanel({
  node,
  windowDays,
  windowKey,
  neighbours,
  doubtful,
  outsideScope,
  onExit,
}: {
  node: BackboneNode;
  doubtful: boolean;
  outsideScope: boolean;
  windowDays: number;
  windowKey: string;
  neighbours: { id: string; name: string; short: string; kind: string; symmetry: string; arrow: string | null; transmissions: number; days: number }[];
  onExit: () => void;
}) {
  const isPhone = useIsPhone();
  const asOf = useAuditAsOf();
  const [showAllNeighbours, setShowAllNeighbours] = useState(false);
  useEffect(() => { setShowAllNeighbours(false); }, [node.native_id]);
  const shownNeighbours = isPhone && !showAllNeighbours ? neighbours.slice(0, 10) : neighbours;
  const cls = backboneNodeClass(node.classes);
  const region = sourceChipTitle("region", node);
  const scope = sourceChipTitle("scope", node);
  const m = node.metrics;
  const repeat = m.repeatability.value !== null ? `${(m.repeatability.value * 100).toFixed(0)}%` : "—";
  // 2026-09-30 fix 1: the underlying score is an uncapped decayed
  // instability figure (can exceed 1.0 with overlapping dropouts, or hit
  // exactly +1.0 flat while still silent at window end) -- clamped here
  // for DISPLAY only, never in the backend's own scoring math.
  const stab = m.stability.value !== null ? `${Math.min(m.stability.value * 100, 100).toFixed(1)}%` : "—";
  // Why not: the per-class summary lines first (the "no backhaul link" line is
  // dropped when the backhaul summary is present); the per-unit lines fold away.
  const hasBackhaulMiss = node.why_not.some((r) => r.key === "backhaul_miss");
  const whyNotSummary = node.why_not.filter((r) => r.key === "regional_backbone_miss" || r.key === "backhaul_miss" || !isWhyNotPerUnit(r.key, hasBackhaulMiss));
  const whyNotPerUnit = node.why_not.filter((r) => isWhyNotPerUnit(r.key, hasBackhaulMiss) && r.key !== "no_backhaul_link");
  const reachVal = backboneReachabilityValue(m.reachability.regions_reached_count, m.reachability.home_scopes.length);

  return (
    <div className="backbone-details">
      <div className="backbone-details-head">
        <h3 tabIndex={-1} aria-label={`${BACKBONE_DETAILS_TITLE} for ${node.name}`} style={{ margin: 0, fontSize: 14 }}>{BACKBONE_DETAILS_TITLE}</h3>
        <button type="button" className="close-btn" onClick={onExit} aria-label={BACKBONE_DETAILS_EXIT_LABEL}>
          &times;
        </button>
      </div>
      <div className="backbone-details-body">
        <div style={{ fontSize: 15, fontWeight: 700 }}>{node.name}</div>
        <div style={{ fontFamily: "monospace", color: "var(--ink-muted)", fontSize: 12, marginBottom: 8 }}>{node.short_id}</div>
        {outsideScope && <div className="settings-field-desc">This selected node is outside the highlighted scope footprint.</div>}
        {backboneClassPills(cls).map((pc) => (
          <span key={pc} className="badge" style={{ background: BACKBONE_CLASS_INFO[pc].color, color: "#17181c", marginRight: 4 }}>
            {BACKBONE_CLASS_INFO[pc].label}
          </span>
        ))}
        {doubtful && (
          <HoverTip text="Every link this node has is longer than the Topology limit, to neighbours that are otherwise well connected. Its reported position is probably wrong, so its links are left out of the distance figures.">
            <span className="topo-tag">Position doubtful</span>
          </HoverTip>
        )}

        {node.qualifying_reasons.length > 0 && (
          <div className="backbone-section">
            <div className="backbone-section-hdr">{BACKBONE_SECTION_WHY}</div>
            <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
              {node.qualifying_reasons.map((r, i) => (
                <li key={i}>
                  {/* 2026-09-30 fix 7: when the inferred data belongs to
                      the FAR-END repeater (this node's own data is
                      confirmed), name that repeater and field instead of
                      the generic suffix, which only makes sense when it's
                      THIS node's own data pointing at its own Region/
                      Scopes section below. */}
                  {backboneReasonLine(r.text, r.key === "backhaul_region_pair" ? "" : r.inferred && r.inferred_detail ? ` ${r.inferred_detail}` : r.inferred ? BACKBONE_REASON_INFERRED_SUFFIX : "")}
                </li>
              ))}
            </ul>
          </div>
        )}
        {node.why_not.length > 0 && (
          <div className="backbone-section">
            <div className="backbone-section-hdr">{BACKBONE_SECTION_WHY_NOT}</div>
            {whyNotSummary.length > 0 && (
              <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
                {whyNotSummary.map((r, i) => (
                  <li key={i}>{r.text}</li>
                ))}
              </ul>
            )}
            {whyNotPerUnit.length > 0 && (
              <details className="backbone-also-judged">
                <summary className="backbone-section-hdr">{`${BACKBONE_WHY_NOT_PER_UNIT} (${whyNotPerUnit.length})`}</summary>
                <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
                  {whyNotPerUnit.map((r, i) => (
                    <li key={i}>{r.text}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        {node.also_judged_in.length > 0 && (
          <details className="backbone-section backbone-also-judged">
            <summary className="backbone-section-hdr">{BACKBONE_SECTION_ALSO_JUDGED}</summary>
            <div className="settings-field-desc">{BACKBONE_SECTION_ALSO_JUDGED_DESC}</div>
            <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
              {node.also_judged_in.map((r, i) => (
                <li key={i}>{r.text}</li>
              ))}
            </ul>
          </details>
        )}

        <div className="backbone-section">
          <div className="backbone-section-hdr">{BACKBONE_SECTION_TRAFFIC}</div>
          <div>
            {node.traffic_share.backbone_share_pct !== null && node.rank_in_region ? (
              (() => {
                const m2 = /^#(\d+) of (\d+) in (.+)$/.exec(node.rank_in_region!);
                if (!m2) return node.rank_in_region;
                return (
                  <span title={backboneTrafficTooltip(m2[3], node.traffic_share.since)}>
                    {backboneTrafficLine(m2[3], node.traffic_share.since, Number(m2[1]), Number(m2[2]))}
                  </span>
                );
              })()
            ) : (
              <span style={{ color: "var(--ink-muted)" }}>{BACKBONE_TRAFFIC_NOT_RANKED}</span>
            )}
          </div>
        </div>

        <div className="backbone-section">
          <div className="backbone-section-hdr"><HoverTip text={BACKBONE_REGION_TIP}><span>{BACKBONE_SECTION_REGION}</span></HoverTip></div>
          {node.verified_region ? (
            <div>
              {node.verified_region.name}
              <span className={`chip backbone-chip-${region.chip}`} title={region.title}>
                {BACKBONE_SOURCE_CHIP_LABEL[region.chip]}
              </span>
            </div>
          ) : (
            <div style={{ color: "var(--ink-muted)" }}>{BACKBONE_REGION_NOT_VERIFIED}</div>
          )}
        </div>

        <div className="backbone-section">
          <div className="backbone-section-hdr"><HoverTip text={BACKBONE_SCOPES_TIP}><span>{BACKBONE_SECTION_SCOPES}</span></HoverTip></div>
          {node.home_scopes_named.length > 0 ? (
            <div>
              {node.home_scopes_named.map((s) => (
                <span key={s.tag} className="backbone-scope-tag" title={s.name}>
                  {s.tag}
                  {s.stale && <span className="audit-scope-stale">{scopeStaleLabel(s.stale_days ?? 0)}</span>}
                </span>
              ))}
              <span className={`chip backbone-chip-${scope.chip}`} title={scope.title}>
                {BACKBONE_SOURCE_CHIP_LABEL[scope.chip]}
              </span>
            </div>
          ) : (
            <div style={{ color: "var(--ink-muted)" }}>{BACKBONE_SCOPES_NONE}</div>
          )}
        </div>

        {node.footprint && (
          <div className="backbone-section">
            <div className="backbone-section-hdr">{BACKBONE_SECTION_FOOTPRINT}</div>
            <div>{FOOTPRINT_LABELS[node.footprint.label] ?? node.footprint.label}{node.footprint.label !== "unknown" ? ` ${node.footprint.within}/${node.footprint.relayed}` : ""}{footprintReasonTag(node.footprint as AuditFootprint) ? ` · ${footprintReasonTag(node.footprint as AuditFootprint)}` : ""}</div>
            <div>Declared: {footprintDeclaredText(node.footprint as AuditFootprint)}</div>
            <div>Relayed: {footprintRelayedText(node.footprint as AuditFootprint)}</div>
          </div>
        )}

        <div className="backbone-section">
          <div className="backbone-section-hdr">{BACKBONE_SECTION_METRICS}</div>
          {metricRow(BACKBONE_METRIC_REPEATABILITY, repeat, BACKBONE_METRIC_REPEATABILITY_CAPTION, BACKBONE_METRIC_REPEATABILITY_CAPTION)}
          {metricRow(BACKBONE_METRIC_STABILITY, stab, BACKBONE_METRIC_STABILITY_CAPTION, BACKBONE_METRIC_STABILITY_CAPTION)}
          {metricRow(BACKBONE_METRIC_CREDIBILITY, backboneCredibilityValue(m.credibility.observers, windowKey), BACKBONE_METRIC_CREDIBILITY_CAPTION, BACKBONE_METRIC_CREDIBILITY_CAPTION)}
          {metricRow(
            BACKBONE_METRIC_REACHABILITY,
            reachVal + (m.reachability.limited_by_observer_coverage ? ` ${BACKBONE_METRIC_REACHABILITY_LIMITED}` : ""),
            BACKBONE_METRIC_REACHABILITY_CAPTION,
            BACKBONE_METRIC_REACHABILITY_CAPTION,
          )}
          {metricRow(BACKBONE_METRIC_LINK_SYMMETRY, String(m.link_symmetry.both_ways_count), BACKBONE_METRIC_LINK_SYMMETRY_CAPTION, BACKBONE_METRIC_LINK_SYMMETRY_CAPTION)}
          {metricRow(BACKBONE_METRIC_BETWEENNESS, backboneBetweennessValue(m.betweenness.rank, m.betweenness.rank_of), BACKBONE_METRIC_BETWEENNESS_CAPTION, BACKBONE_METRIC_BETWEENNESS_CAPTION)}
        </div>

        <div className="backbone-section">
          <div className="backbone-section-hdr">{BACKBONE_SECTION_LAST_HEARD}</div>
          <div>
            {relativeTime(node.last_heard, asOf)} · {node.observer_count} observers
          </div>
        </div>

        <div className="backbone-section-hdr">{BACKBONE_SECTION_NEIGHBOURS(neighbours.length)}</div>
        {neighbours.length === 0 ? (
          <div style={{ color: "var(--ink-muted)" }}>{BACKBONE_NEIGHBOURS_NONE}</div>
        ) : (
          shownNeighbours.map((n) => (
            <div key={n.id} className="backbone-neighbour">
              <div>
                <div style={{ fontWeight: 600 }}>
                  {truncateWordBoundary(n.name, 22)} <span style={{ fontFamily: "monospace", color: "var(--ink-muted)", fontSize: 11 }}>{n.short}</span>
                </div>
                <span className="backbone-neighbour-sym">{backboneNeighbourSymmetryLabel(n.symmetry === "both_ways", n.arrow)}</span>
              </div>
              <div className="settings-field-desc" style={{ textAlign: "right" }}>
                {backboneNeighbourMeta(n.transmissions, n.days, windowDays)}
              </div>
            </div>
          ))
        )}
        {isPhone && !showAllNeighbours && neighbours.length > 10 && (
          <button type="button" className="rf-btn rf-btn-outline" onClick={() => setShowAllNeighbours(true)}>
            {BACKBONE_NEIGHBOURS_SHOW_ALL(neighbours.length)}
          </button>
        )}
      </div>
    </div>
  );
}

// Topology tab (2026-10-06): this is the old Backbone tab. The tab id
// "backbone", the route /api/audit/backbone and the snapshot kind stay as they
// are (historical names, kept so URLs and saved views keep working).
export function TopologyTab({ windowKey, onWindowChange }: { windowKey: AuditWindow; onWindowChange: (w: AuditWindow) => void }) {
  const asOf = useAuditAsOf();
  const { takenAt } = useAuditHistory();
  const { displaySettings } = useAppData();
  const distPrefRef = useRef<DistanceUnitPref | undefined>(undefined);
  distPrefRef.current = displaySettings?.display.distance_unit;
  const [data, setData] = useState<BackboneResponse | null | "unavailable" | "pending">(null);
  const [selectedScope, setSelectedScope] = useState("");
  const [scopeLoad, setScopeLoad] = useState<"idle" | "loading" | "error">("idle");
  const [scopeRetry, setScopeRetry] = useState(0);
  const scopeRetryRef = useRef(false);
  const [unpositionedQuery, setUnpositionedQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showNeither, setShowNeither] = useState(true);
  const [showFaint, setShowFaint] = useState(false);
  const [showNeighbour, setShowNeighbour] = useState(false);
  const scopeFootprint = data && data !== "unavailable" && data !== "pending" && data.scope_footprint?.scope === selectedScope
    ? data.scope_footprint
    : null;
  // 2026-09-30 fix 2: collapsed by default, persisted per browser -- same
  // guarded try/catch pattern as AppData.tsx's theme/protocol-filter
  // persistence (private-mode/blocked storage must never break the tab,
  // just fail to remember the choice).
  const [legendOpen, setLegendOpenState] = useState(() => {
    // Phones start closed (the open legend covers the map); desktop keeps its saved choice.
    if (readIsPhone()) return false;
    try {
      return localStorage.getItem("amalgamesh:backboneLegendOpen") === "true";
    } catch {
      return false;
    }
  });
  const setLegendOpen = (open: boolean) => {
    setLegendOpenState(open);
    try {
      localStorage.setItem("amalgamesh:backboneLegendOpen", open ? "true" : "false");
    } catch {
      /* private-mode / blocked storage: fine, just don't persist */
    }
  };

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const [mapReady, setMapReady] = useState(0);
  // 2026-09-30 fix 4: which pill is currently hovered, if any -- mutated
  // directly by the map's own hover handlers (never through React state,
  // so a hover doesn't trigger a re-render on every mousemove). Read by
  // applyPillSort below to decide which pill draws above its overlapping
  // neighbours.
  const hoveredIdRef = useRef<string | null>(null);
  // The camera after the user's own pan/zoom (a moveend with an originalEvent).
  // Kept across window changes, which rebuild the map; null until the user
  // moves it, in which case the map fits to the linked nodes.
  const userCameraRef = useRef<{ center: [number, number]; zoom: number } | null>(null);
  const stateRef = useRef({ selectedId, showNeither, showFaint, showNeighbour, selectedScope, scopeReady: !!scopeFootprint && scopeFootprint.scope === selectedScope });
  stateRef.current = { selectedId, showNeither, showFaint, showNeighbour, selectedScope, scopeReady: !!scopeFootprint && scopeFootprint.scope === selectedScope };

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setSelectedId(null);
    setSelectedScope("");
    // 2026-09-30 Stage B: the route is snapshot-only now -- a brand-new
    // install (nothing has ever completed a background pass yet) answers
    // `{ pending: true, ... }` instead of a real BackboneResponse; see
    // audit/snapshot_serving.py's own module docstring.
    tryApiGet<BackboneResponse | { pending: true }>(`/api/audit/backbone?window=${windowKey}${historyQuery(takenAt)}`)
      .then((res) => {
        if (cancelled) return;
        if (res && "pending" in res && res.pending) {
          setData("pending");
        } else {
          setData((res as BackboneResponse | null) ?? "unavailable");
        }
      })
      .catch(() => !cancelled && setData("unavailable"));
    return () => {
      cancelled = true;
    };
  }, [windowKey, takenAt]);

  useEffect(() => {
    if (!selectedScope) {
      setScopeLoad("idle");
      return;
    }
    if (!scopeRetryRef.current && data && data !== "unavailable" && data !== "pending" && data.scope_footprint?.scope === selectedScope) {
      setScopeLoad("idle");
      return;
    }
    scopeRetryRef.current = false;
    let cancelled = false;
    setScopeLoad("loading");
    tryApiGet<BackboneResponse | { pending: true }>(`/api/audit/backbone?window=${windowKey}${historyQuery(takenAt)}&scope=${encodeURIComponent(selectedScope)}`)
      .then((res) => {
        if (cancelled) return;
        if (!res || "pending" in res || !res.scope_footprint || res.scope_footprint.scope !== selectedScope ||
          !Array.isArray(res.nodes) || !Array.isArray(res.links)) {
          setScopeLoad("error");
          return;
        }
        // Use one report generation for both the map base arrays and scope
        // membership. A background refresh between the two reads must never
        // leave the UI stuck on a permanently mismatched footprint.
        setData(res);
        setSelectedId((current) => current && res.nodes.some((n) => n.native_id === current) ? current : null);
        setScopeLoad("idle");
      })
      .catch(() => !cancelled && setScopeLoad("error"));
    return () => { cancelled = true; };
  }, [windowKey, takenAt, selectedScope, data, scopeRetry]);

  const nodesById = useMemo(() => {
    const map = new Map<string, BackboneNode>();
    if (data && data !== "unavailable" && data !== "pending") for (const n of data.nodes) map.set(n.native_id, n);
    return map;
  }, [data]);

  const scopeOptions = useMemo(() => {
    const scopes = new Set<string>();
    if (data && data !== "unavailable" && data !== "pending") {
      for (const n of data.nodes) {
        for (const s of n.home_scopes ?? []) if (s) scopes.add(s);
        for (const s of n.metrics?.reachability?.relayed_scopes ?? []) if (s) scopes.add(s);
      }
      for (const l of data.links) for (const s of l.scopes_carried ?? []) if (s) scopes.add(s);
    }
    if (selectedScope) scopes.add(selectedScope);
    return [...scopes].sort((a, b) => a.localeCompare(b));
  }, [data, selectedScope]);
  const scopeNodeIds = useMemo(() => new Set(scopeFootprint?.repeaters ?? []), [scopeFootprint]);
  const scopeLinkKeys = useMemo(() => new Set((scopeFootprint?.links ?? []).map((l) => [l.a, l.b].sort().join("\u0000"))), [scopeFootprint]);

  // Every positioned repeater becomes its own GeoJSON point feature — no
  // clustering (see this file's own header comment for why).
  const { classifiedFC, neitherFC, pillProminentIds } = useMemo(() => {
    const classifiedFeatures: GJFeature<GJPoint, NodeProps>[] = [];
    const neitherFeatures: GJFeature<GJPoint, NodeProps>[] = [];
    const prominent = new Set<string>();
    if (data && data !== "unavailable" && data !== "pending") {
      const byRegion = new Map<string, BackboneNode[]>();
      for (const n of data.nodes) {
        const cls = backboneNodeClass(n.classes);
        if (cls === "neither") continue;
        const key = n.verified_region?.code ?? n.region_provenance?.code ?? "\u0000none";
        const list = byRegion.get(key) ?? [];
        list.push(n);
        byRegion.set(key, list);
      }
      for (const list of byRegion.values()) {
        const ranked = [...list].sort((a, b) => (b.traffic_share.backbone_share_pct ?? -1) - (a.traffic_share.backbone_share_pct ?? -1));
        for (const n of ranked.slice(0, 2)) prominent.add(n.native_id);
      }
      for (const n of data.nodes) {
        if (n.lat === null || n.lon === null) continue;
        const cls = backboneNodeClass(n.classes);
        const props: NodeProps = {
          id: n.native_id,
          name: n.name,
          short_id: n.short_id,
          name_short22: truncateWordBoundary(n.name, 22),
          cls,
          pill_prominent: cls === "both" || prominent.has(n.native_id),
          in_scope: !selectedScope || !scopeFootprint || scopeFootprint.scope !== selectedScope || scopeNodeIds.has(n.native_id),
        };
        const feature: GJFeature<GJPoint, NodeProps> = {
          type: "Feature",
          geometry: { type: "Point", coordinates: [n.lon, n.lat] },
          properties: props,
        };
        if (cls === "neither") neitherFeatures.push(feature);
        else classifiedFeatures.push(feature);
      }
    }
    return {
      classifiedFC: { type: "FeatureCollection" as const, features: classifiedFeatures },
      neitherFC: { type: "FeatureCollection" as const, features: neitherFeatures },
      pillProminentIds: prominent,
    };
  }, [data, selectedScope, scopeFootprint, scopeNodeIds]);

  const linksFC = useMemo(() => {
    const features: GJFeature<GJLineString, LinkProps>[] = [];
    if (data && data !== "unavailable" && data !== "pending") {
      for (const l of data.links) {
        if (!l.stable) continue;
        const a = nodesById.get(l.a);
        const b = nodesById.get(l.b);
        if (!a || !b || a.lat === null || a.lon === null || b.lat === null || b.lon === null) continue;
        const props: LinkProps = {
          kind: linkKind(l),
          symmetry: l.symmetry,
          direction: l.direction,
          a: l.a,
          b: l.b,
          a_name: a.name,
          b_name: b.name,
          transmissions: l.transmissions,
          days: l.days_seen,
          distance_km: l.distance_km,
          position_note: l.position_note,
          in_scope: !selectedScope || !scopeFootprint || scopeFootprint.scope !== selectedScope || scopeLinkKeys.has([l.a, l.b].sort().join("\u0000")),
        };
        features.push({
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [
              [a.lon, a.lat],
              [b.lon, b.lat],
            ],
          },
          properties: props,
        });
      }
    }
    return { type: "FeatureCollection" as const, features };
  }, [data, nodesById, selectedScope, scopeFootprint, scopeLinkKeys]);

  // Neighbour links (origin to first hop, last hop to observer): stable ones
  // only, drawn as dotted blue lines when the Legend toggle is on. A payload
  // stored before this field existed simply has none.
  const neighbourFC = useMemo(() => {
    const features: GJFeature<GJLineString, Record<string, unknown>>[] = [];
    if (data && data !== "unavailable" && data !== "pending" && data.neighbour_links && data.neighbour_nodes) {
      for (const l of data.neighbour_links) {
        if (!l.stable) continue;
        const a = data.neighbour_nodes[l.a];
        const b = data.neighbour_nodes[l.b];
        if (!a || !b || a.lat === null || a.lon === null || b.lat === null || b.lon === null) continue;
        features.push({
          type: "Feature",
          geometry: { type: "LineString", coordinates: [[a.lon, a.lat], [b.lon, b.lat]] },
          properties: { kind: l.kind, a: l.a, b: l.b },
        });
      }
    }
    return { type: "FeatureCollection" as const, features };
  }, [data]);

  // Detail adjacency uses all stable report links, including links whose
  // endpoints lack coordinates. Only the map geometry below requires positions.
  const adjacency = useMemo(() => {
    const adj = new Map<string, { id: string; short: string; name: string; kind: string; symmetry: string; arrow: string | null; transmissions: number; days: number; distance_km: number | null }[]>();
    if (!data || data === "unavailable" || data === "pending") return adj;
    for (const link of data.links) {
      if (!link.stable) continue;
      const p = { ...link, kind: linkKind(link) };
      const oneWay = p.symmetry !== "both_ways";
      let arrowForA: string | null = null;
      let arrowForB: string | null = null;
      if (oneWay) {
        if (p.direction === "a_to_b") {
          arrowForA = "→";
          arrowForB = "←";
        } else if (p.direction === "b_to_a") {
          arrowForA = "←";
          arrowForB = "→";
        }
      }
      const bNode = nodesById.get(p.b);
      const aNode = nodesById.get(p.a);
      const listA = adj.get(p.a) ?? [];
      listA.push({ id: p.b, short: bNode?.short_id ?? "", name: bNode?.name ?? p.b, kind: p.kind, symmetry: p.symmetry, arrow: arrowForA, transmissions: p.transmissions, days: p.days_seen, distance_km: p.distance_km });
      adj.set(p.a, listA);
      const listB = adj.get(p.b) ?? [];
      listB.push({ id: p.a, short: aNode?.short_id ?? "", name: aNode?.name ?? p.a, kind: p.kind, symmetry: p.symmetry, arrow: arrowForB, transmissions: p.transmissions, days: p.days_seen, distance_km: p.distance_km });
      adj.set(p.b, listB);
    }
    return adj;
  }, [data, nodesById]);

  const counts = useMemo(() => {
    let both = 0;
    let backhaulOnly = 0;
    let regionalOnly = 0;
    let other = 0;
    let positioned = 0;
    if (data && data !== "unavailable" && data !== "pending") {
      for (const n of data.nodes) {
        if (n.lat === null || n.lon === null) continue;
        positioned += 1;
        const cls = backboneNodeClass(n.classes);
        if (cls === "both") both += 1;
        else if (cls === "backhaul") backhaulOnly += 1;
        else if (cls === "regional_backbone") regionalOnly += 1;
        else other += 1;
      }
    }
    return { both, backhaulOnly, regionalOnly, other, positioned };
  }, [data]);

  const windowDays = useMemo(() => {
    if (!data || data === "unavailable" || data === "pending") return 7;
    const ms = new Date(data.window_end).getTime() - new Date(data.window_start).getTime();
    return Math.max(1, Math.round(ms / 86400000));
  }, [data]);

  const unpositionedNodes = useMemo(() => {
    if (!data || data === "unavailable" || data === "pending") return [];
    const query = unpositionedQuery.trim().toLocaleLowerCase();
    return data.nodes.filter((n) => (n.lat === null || n.lon === null) &&
      (!query || `${n.name} ${n.short_id} ${n.native_id}`.toLocaleLowerCase().includes(query)));
  }, [data, unpositionedQuery]);

  // QA/debug hook, same spirit as `window.backboneMap` above: the exact
  // GeoJSON fed into the map's sources, for a browser test to read
  // directly rather than reach into MapLibre's private internals.
  useEffect(() => {
    (window as unknown as { backboneDebug?: unknown }).backboneDebug = { classifiedFC, neitherFC, linksFC };
  }, [classifiedFC, neitherFC, linksFC]);

  // --- Map build: only rebuilds on a genuinely new dataset, never on
  // selection/toggle changes (those mutate the existing map's layers/
  // sources instead — see the effect below). Always the same light,
  // MeshMapper-like basemap regardless of the app's dark-mode toggle (see
  // buildBackboneMapStyle's own comment). ---------------------------------
  useEffect(() => {
    if (!containerRef.current) return;
    if (!data || data === "unavailable" || data === "pending") return;

    const map = new MLMap({
      container: containerRef.current,
      style: buildBackboneMapStyle(),
      center: userCameraRef.current?.center ?? [-114.5, 43.0],
      zoom: userCameraRef.current?.zoom ?? 6,
      attributionControl: { compact: true },
    });
    mapRef.current = map;
    // Exposed the same way the approved prototype did (`window.map = map`)
    // — a read-only debug/QA hook for driving real zoom/click interactions
    // against the live map instance, not used by any app code.
    (window as unknown as { backboneMap?: MLMap }).backboneMap = map;
    map.addControl(new NavigationControl({ showCompass: false }), "top-right");

    const hoverPopup = new Popup({ closeButton: false, closeOnClick: false, offset: 14 });
    map.on("moveend", (e) => {
      if ((e as unknown as { originalEvent?: unknown }).originalEvent) {
        const c = map.getCenter();
        userCameraRef.current = { center: [c.lng, c.lat], zoom: map.getZoom() };
      }
    });

    map.on("load", () => {
      const classes: BackboneClass[] = ["both", "backhaul", "regional_backbone", "neither"];
      for (const cls of classes) {
        const info = BACKBONE_CLASS_INFO[cls];
        const img = makePillImage(BACKBONE_PILL_FILL_COLOR, info.color);
        if (!map.hasImage(`pill-${cls}`)) {
          map.addImage(`pill-${cls}`, img.data, {
            content: [img.r, 5, img.w - img.r, img.h - 5],
            stretchX: [[img.r, img.w - img.r]],
            stretchY: [[img.h / 2 - 1, img.h / 2 + 1]],
          });
        }
      }

      map.addSource("links", { type: "geojson", data: linksFC as unknown as GJFeatureCollection });
      map.addLayer({
        id: "links-faint",
        type: "line",
        source: "links",
        filter: ["==", ["get", "kind"], "faint"],
        paint: { "line-color": BACKBONE_LINK_KIND_INFO.faint.color, "line-width": BACKBONE_LINK_KIND_INFO.faint.width, "line-opacity": 0.25 },
        layout: { visibility: "none" },
      });
      map.addLayer({
        id: "links-backbone",
        type: "line",
        source: "links",
        filter: ["==", ["get", "kind"], "backbone"],
        paint: { "line-color": BACKBONE_LINK_KIND_INFO.backbone.color, "line-width": BACKBONE_LINK_KIND_INFO.backbone.width, "line-opacity": 0.9 },
      });
      map.addLayer({
        id: "links-backhaul",
        type: "line",
        source: "links",
        filter: ["==", ["get", "kind"], "backhaul"],
        paint: { "line-color": BACKBONE_LINK_KIND_INFO.backhaul.color, "line-width": BACKBONE_LINK_KIND_INFO.backhaul.width, "line-opacity": 0.95 },
      });

      map.addSource("neighbour-links", { type: "geojson", data: neighbourFC as unknown as GJFeatureCollection });
      map.addLayer({
        id: "neighbour-links",
        type: "line",
        source: "neighbour-links",
        layout: { visibility: stateRef.current.showNeighbour ? "visible" : "none", "line-cap": "round" },
        paint: { "line-color": NEIGHBOUR_LINK_COLOR, "line-width": 2, "line-opacity": 0.85, "line-dasharray": [0.1, 2] },
      });

      map.addSource("detail-links", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      const detailPaint = {
        "line-color": ["match", ["get", "kind"], "backhaul", BACKBONE_LINK_KIND_INFO.backhaul.color, "backbone", BACKBONE_LINK_KIND_INFO.backbone.color, BACKBONE_LINK_KIND_INFO.faint.color] as unknown as string,
        "line-width": ["match", ["get", "kind"], "backhaul", BACKBONE_LINK_KIND_INFO.backhaul.width + 0.6, "backbone", BACKBONE_LINK_KIND_INFO.backbone.width + 0.6, BACKBONE_LINK_KIND_INFO.faint.width + 0.6] as unknown as number,
        "line-opacity": 0.95,
      };
      map.addLayer({ id: "detail-links-solid", type: "line", source: "detail-links", filter: ["!=", ["get", "symmetry"], "one_way"], paint: detailPaint as never });
      map.addLayer({ id: "detail-links-dashed", type: "line", source: "detail-links", filter: ["==", ["get", "symmetry"], "one_way"], paint: { ...detailPaint, "line-dasharray": [2, 1.6] } as never });

      map.addSource("classified", { type: "geojson", data: classifiedFC as unknown as GJFeatureCollection });
      map.addSource("neither", { type: "geojson", data: (stateRef.current.showNeither ? neitherFC : { type: "FeatureCollection", features: [] }) as unknown as GJFeatureCollection });

      // Transparent 12 px hit targets under the dots: they carry the click and
      // hover handlers so a dot is easy to hit on desktop and phone.
      const hitPaint = { "circle-radius": 12, "circle-color": "rgba(0,0,0,0)", "circle-stroke-width": 0 } as never;
      map.addLayer({ id: "neither-hit", type: "circle", source: "neither", paint: hitPaint });
      map.addLayer({ id: "classified-hit", type: "circle", source: "classified", paint: hitPaint });
      map.addLayer({
        id: "neither-dot",
        type: "circle",
        source: "neither",
        paint: { "circle-radius": 3, "circle-color": BACKBONE_CLASS_INFO.neither.color, "circle-stroke-width": 1, "circle-stroke-color": "#ffffff" },
      });
      map.addLayer({
        id: "classified-dot",
        type: "circle",
        source: "classified",
        paint: {
          "circle-radius": 6,
          // 2026-09-30 pill-colour task: the dot's fill IS the class
          // colour (same value the pill's own outline uses) — one shared,
          // neutral ring colour (BACKBONE_DOT_STROKE_COLOR) is what
          // outlines it, not a second per-class shade.
          "circle-color": ["match", ["get", "cls"], "both", BACKBONE_CLASS_INFO.both.color, "backhaul", BACKBONE_CLASS_INFO.backhaul.color, "regional_backbone", BACKBONE_CLASS_INFO.regional_backbone.color, BACKBONE_CLASS_INFO.neither.color],
          "circle-stroke-width": 1.5,
          "circle-stroke-color": BACKBONE_DOT_STROKE_COLOR,
        } as never,
      });
      const pillLayout = {
        "icon-image": ["concat", "pill-", ["get", "cls"]],
        "icon-text-fit": "both",
        "icon-text-fit-padding": [3, 6, 3, 6],
        "text-field": ["concat", ["get", "name_short22"], " · ", ["get", "short_id"]],
        "text-size": 9.5,
        "text-max-width": 40,
        "text-allow-overlap": false,
        "icon-allow-overlap": false,
        // 2026-09-30 fix 4: 0 by default; applyPillSort below rewrites
        // this per layer to single out whichever pill is hovered or
        // selected, so THAT one wins collision and renders above its
        // overlapping neighbours instead of an arbitrary one of them.
        "symbol-sort-key": 0,
      } as never;
      // 2026-09-30 pill-colour task: white text on every pill (they're
      // always black-filled now, regardless of class) with a black halo
      // matching the pill fill, for crisp edges against the basemap.
      const pillPaint = { "text-color": BACKBONE_PILL_TEXT_COLOR, "text-halo-color": BACKBONE_PILL_FILL_COLOR, "text-halo-width": 1 } as never;
      map.addLayer({ id: "classified-pill", type: "symbol", source: "classified", filter: ["==", ["get", "pill_prominent"], true], layout: pillLayout, paint: pillPaint });
      map.addLayer({ id: "classified-pill-secondary", type: "symbol", source: "classified", filter: ["==", ["get", "pill_prominent"], false], minzoom: PILL_PROMINENT_MIN_ZOOM, layout: pillLayout, paint: pillPaint });
      map.addLayer({
        id: "neither-label",
        type: "symbol",
        source: "neither",
        minzoom: NEITHER_LABEL_MIN_ZOOM,
        layout: { "icon-image": "pill-neither", "icon-text-fit": "both", "icon-text-fit-padding": [3, 6, 3, 6], "text-field": ["get", "short_id"], "text-size": 9.5, "text-allow-overlap": false, "icon-allow-overlap": false, "symbol-sort-key": 0 } as never,
        paint: pillPaint,
      });
      // Selected node: its own pill layers, added last so they draw on top.
      // No minimum zoom and overlap allowed, so the selected pill is always
      // shown. The style has no bold font face (no glyph source), so a
      // thicker white halo makes the text heavier instead.
      const selFilter = ["==", ["get", "id"], stateRef.current.selectedId ?? ""] as never;
      const selPaint = { "text-color": BACKBONE_PILL_TEXT_COLOR, "text-halo-color": "#ffffff", "text-halo-width": 0.9 } as never;
      map.addLayer({
        id: "selected-pill",
        type: "symbol",
        source: "classified",
        filter: selFilter,
        layout: { ...(pillLayout as unknown as Record<string, unknown>), "text-size": 12, "text-allow-overlap": true, "icon-allow-overlap": true, "symbol-sort-key": 0 } as never,
        paint: selPaint,
      });
      map.addLayer({
        id: "selected-pill-neither",
        type: "symbol",
        source: "neither",
        filter: selFilter,
        layout: { "icon-image": "pill-neither", "icon-text-fit": "both", "icon-text-fit-padding": [3, 6, 3, 6], "text-field": ["get", "short_id"], "text-size": 12, "text-allow-overlap": true, "icon-allow-overlap": true } as never,
        paint: selPaint,
      });


      // A stray bad position (some repeaters wrongly report GPS 0,0 or
      // another far-off coordinate — ReachabilityTable's own comment notes
      // the same thing) must never blow the initial fit out to a
      // world-wide view. Same plausible-CONUS-ish bbox the prototype used
      // for this exact reason.
      // Fit to the nodes that have a link in this window (both ends of every
      // drawn link); with no links at all, to every positioned node. Skipped
      // when the user has already moved the map (their camera is kept).
      const allCoords: [number, number][] = [];
      for (const f of linksFC.features) for (const c of f.geometry.coordinates) allCoords.push(c as [number, number]);
      if (allCoords.length === 0) {
        for (const f of classifiedFC.features) allCoords.push(f.geometry.coordinates as [number, number]);
        for (const f of neitherFC.features) allCoords.push(f.geometry.coordinates as [number, number]);
      }
      const plausible = allCoords.filter(([lon, lat]) => isPlausibleCoord(lon, lat));
      const fitCoords = plausible.length > 0 ? plausible : allCoords;
      if (fitCoords.length > 0 && userCameraRef.current === null) {
        let bounds = new LngLatBounds(fitCoords[0], fitCoords[0]);
        for (const c of fitCoords) bounds.extend(c);
        map.fitBounds(bounds, { padding: 40, duration: 0 });
      }

      function updateVisibility() {
        const st = stateRef.current;
        const detailMode = st.selectedId !== null;
        const atRegionZoom = map.getZoom() >= REGION_ZOOM;
        // With Details open the other links stay drawn but dimmed (applyLinkDimming); the selected node's own links are redrawn heavier by detail-links.
        map.setLayoutProperty("links-backbone", "visibility", atRegionZoom ? "visible" : "none");
        map.setLayoutProperty("links-backhaul", "visibility", "visible");
        map.setLayoutProperty("links-faint", "visibility", atRegionZoom && st.showFaint ? "visible" : "none");
        map.setLayoutProperty("neighbour-links", "visibility", st.showNeighbour ? "visible" : "none");
        map.setLayoutProperty("detail-links-solid", "visibility", detailMode ? "visible" : "none");
        map.setLayoutProperty("detail-links-dashed", "visibility", detailMode ? "visible" : "none");
        applyLinkDimming();
      }
      function applyLinkDimming() {
        const st = stateRef.current;
        const base: Record<string, number> = { "links-faint": 0.25, "links-backbone": 0.9, "links-backhaul": 0.95 };
        for (const layerId of Object.keys(base)) {
          const b = base[layerId];
          const selectedExpr = st.selectedId === null ? b : (["case", ["any", ["==", ["get", "a"], st.selectedId], ["==", ["get", "b"], st.selectedId]], b, 0.1] as never);
          const expr = st.selectedScope && st.scopeReady ? (["case", ["==", ["get", "in_scope"], true], selectedExpr, 0.1] as never) : selectedExpr;
          map.setPaintProperty(layerId, "line-opacity", expr);
        }
      }
      function applySelectionDimming() {
        const st = stateRef.current;
        const selectedExpr = st.selectedId === null ? 1 : (["case", ["==", ["get", "id"], st.selectedId], 1, 0.18] as never);
        const dimExpr = st.selectedScope && st.scopeReady ? (["case", ["==", ["get", "in_scope"], true], selectedExpr, 0.12] as never) : selectedExpr;
        for (const layerId of ["classified-dot", "neither-dot"]) map.setPaintProperty(layerId, "circle-opacity", dimExpr as never);
        for (const layerId of ["classified-dot", "neither-dot"]) map.setPaintProperty(layerId, "circle-stroke-opacity", dimExpr as never);
        for (const layerId of ["classified-pill", "classified-pill-secondary", "neither-label"]) {
          map.setPaintProperty(layerId, "icon-opacity", dimExpr as never);
          map.setPaintProperty(layerId, "text-opacity", dimExpr as never);
        }
      }
      function applyDetailDimming() {
        const st = stateRef.current;
        const opacity = st.selectedScope && st.scopeReady
          ? (["case", ["==", ["get", "in_scope"], true], 0.95, 0.12] as never)
          : 0.95;
        for (const layerId of ["detail-links-solid", "detail-links-dashed"]) map.setPaintProperty(layerId, "line-opacity", opacity as never);
      }
      // 2026-09-30 fix 4: whichever pill is hovered (falling back to the
      // selected one, if any) gets a higher symbol-sort-key than every
      // other pill, so where truncated pills overlap, THAT one wins
      // MapLibre's own collision system and renders on top rather than an
      // arbitrary one of the overlapping set.
      function applyPillSort() {
        const topId = hoveredIdRef.current ?? stateRef.current.selectedId;
        const expr = (topId ? ["case", ["==", ["get", "id"], topId], 1, 0] : 0) as never;
        for (const layerId of ["classified-pill", "classified-pill-secondary", "neither-label"]) {
          map.setLayoutProperty(layerId, "symbol-sort-key", expr);
        }
        // The selected node is drawn by its own top-most pill layers.
        const selFilterNow = ["==", ["get", "id"], stateRef.current.selectedId ?? ""] as never;
        map.setFilter("selected-pill", selFilterNow);
        map.setFilter("selected-pill-neither", selFilterNow);
      }
      (map as unknown as { _backboneUpdateVisibility: () => void })._backboneUpdateVisibility = updateVisibility;
      (map as unknown as { _backboneApplyLinkDimming: () => void })._backboneApplyLinkDimming = applyLinkDimming;
      (map as unknown as { _backboneApplySelectionDimming: () => void })._backboneApplySelectionDimming = applySelectionDimming;
      (map as unknown as { _backboneApplyDetailDimming: () => void })._backboneApplyDetailDimming = applyDetailDimming;
      (map as unknown as { _backboneApplyPillSort: () => void })._backboneApplyPillSort = applyPillSort;

      updateVisibility();
      applySelectionDimming();
      applyPillSort();

      function wireHoverPoint(layerId: string) {
        map.on("mouseenter", layerId, (e) => {
          map.getCanvas().style.cursor = "pointer";
          const f = e.features?.[0] as MapGeoJSONFeature | undefined;
          if (!f) return;
          const p = f.properties as unknown as NodeProps;
          hoverPopup.setLngLat((f.geometry as GJPoint).coordinates as [number, number]).setHTML(pillTooltipHtml(p.name, p.short_id)).addTo(map);
          hoveredIdRef.current = p.id;
          applyPillSort();
        });
        map.on("mousemove", layerId, (e) => {
          const f = e.features?.[0] as MapGeoJSONFeature | undefined;
          if (f) hoverPopup.setLngLat((f.geometry as GJPoint).coordinates as [number, number]);
        });
        map.on("mouseleave", layerId, () => {
          map.getCanvas().style.cursor = "";
          hoverPopup.remove();
          hoveredIdRef.current = null;
          applyPillSort();
        });
        map.on("click", layerId, (e) => {
          const f = e.features?.[0] as MapGeoJSONFeature | undefined;
          const p = f?.properties as unknown as NodeProps | undefined;
          hoverPopup.remove();
          if (p) selectRef.current(p.id);
        });
      }
      for (const layerId of ["classified-hit", "classified-pill", "classified-pill-secondary", "neither-hit", "neither-label", "selected-pill", "selected-pill-neither"]) wireHoverPoint(layerId);

      const POINT_HIT_LAYERS = ["classified-hit", "neither-hit", "classified-pill", "classified-pill-secondary", "neither-label", "selected-pill", "selected-pill-neither"];
      function wireHoverLine(layerId: string) {
        map.on("mouseenter", layerId, () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mousemove", layerId, (e) => {
          // A dot or label under the cursor wins: leave its popup alone.
          if (map.queryRenderedFeatures(e.point, { layers: POINT_HIT_LAYERS }).length > 0) return;
          const f = e.features?.[0] as MapGeoJSONFeature | undefined;
          if (f) hoverPopup.setLngLat(e.lngLat).setHTML(linkTooltipHtml(f.properties as unknown as LinkProps, distPrefRef.current)).addTo(map);
        });
        map.on("mouseleave", layerId, () => {
          map.getCanvas().style.cursor = "";
          if (hoveredIdRef.current === null) hoverPopup.remove();
        });
      }
      for (const layerId of ["links-backbone", "links-backhaul", "links-faint", "detail-links-solid", "detail-links-dashed"]) wireHoverLine(layerId);

      map.on("zoomend", updateVisibility);
      setMapReady((n) => n + 1);
    });

    return () => {
      hoverPopup.remove();
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const selectRef = useRef<(id: string | null) => void>(() => {});
  selectRef.current = (id: string | null) => setSelectedId(id);

  // Interaction effect: updates the ALREADY-BUILT map's sources/layers when
  // selection or the two toggles change, without ever rebuilding the map
  // itself (which would reset the camera on every click).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const neitherSrc = map.getSource("neither") as GeoJSONSource | undefined;
    neitherSrc?.setData((showNeither ? neitherFC : { type: "FeatureCollection", features: [] }) as unknown as GJFeatureCollection);
    (map.getSource("classified") as GeoJSONSource | undefined)?.setData(classifiedFC as unknown as GJFeatureCollection);
    (map.getSource("links") as GeoJSONSource | undefined)?.setData(linksFC as unknown as GJFeatureCollection);

    const neighbourSrc = map.getSource("neighbour-links") as GeoJSONSource | undefined;
    neighbourSrc?.setData(neighbourFC as unknown as GJFeatureCollection);

    const selNode = selectedId ? nodesById.get(selectedId) : null;

    const detailSrc = map.getSource("detail-links") as GeoJSONSource | undefined;
    if (selectedId && selNode) {
      const neigh = adjacency.get(selectedId) ?? [];
      const features: GJFeature<GJLineString, Record<string, unknown>>[] = [];
      for (const n of neigh) {
        const other = nodesById.get(n.id);
        if (!other || selNode.lat === null || selNode.lon === null || other.lat === null || other.lon === null) continue;
        features.push({
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [
              [selNode.lon!, selNode.lat!],
              [other.lon, other.lat],
            ],
          },
          properties: {
            kind: n.kind,
            symmetry: n.symmetry,
            a: selectedId,
            b: n.id,
            in_scope: !selectedScope || !scopeFootprint || scopeLinkKeys.has([selectedId, n.id].sort().join("\u0000")),
          },
        });
      }
      detailSrc?.setData({ type: "FeatureCollection", features });
      if (selNode.lat !== null && selNode.lon !== null) map.easeTo({ center: [selNode.lon, selNode.lat], duration: 300 });
    } else {
      detailSrc?.setData({ type: "FeatureCollection", features: [] });
    }

    const anyMap = map as unknown as { _backboneUpdateVisibility?: () => void; _backboneApplySelectionDimming?: () => void; _backboneApplyLinkDimming?: () => void; _backboneApplyDetailDimming?: () => void; _backboneApplyPillSort?: () => void };
    anyMap._backboneUpdateVisibility?.();
    anyMap._backboneApplySelectionDimming?.();
    anyMap._backboneApplyLinkDimming?.();
    anyMap._backboneApplyDetailDimming?.();
    anyMap._backboneApplyPillSort?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, selectedId, selectedScope, showNeither, showFaint, showNeighbour, classifiedFC, linksFC, neitherFC, neighbourFC, adjacency, nodesById, scopeFootprint, scopeLinkKeys]);

  const selectedNode = selectedId ? nodesById.get(selectedId) ?? null : null;
  const doubtfulIds = useMemo(() => {
    const out = new Set<string>();
    if (data && data !== "unavailable" && data !== "pending") {
      for (const p of data.distance?.doubtful_positions ?? []) out.add(p.node_id.toLowerCase());
    }
    return out;
  }, [data]);
  const selectedNeighbours = useMemo(() => {
    if (!selectedId) return [];
    return (adjacency.get(selectedId) ?? []).slice().sort((a, b) => b.transmissions - a.transmissions);
  }, [selectedId, adjacency]);

  // 2026-09-30 layout task: the newest-data staleness check now drives a
  // single, conditionally-shown warning line (see backboneStaleWarning's
  // own comment) instead of always appending onto an always-shown "Live
  // data, ... UTC" line — `null` here means the data is fresh enough that
  // nothing about its age needs to be said at all.
  const staleWarning = data && data !== "unavailable" && data !== "pending" ? backboneStaleWarning(data.newest_event_at, data.settings.stale_after_hours, asOf) : null;

  return (
    <section className="page-section">
      {/* 2026-09-30 layout task: the "BACKBONE" heading and the intro
          description box are gone from the page body — the intro's own
          text now lives as a hover tooltip on the Backbone sub-tab button
          (AuditPage.tsx's own tab row, BACKBONE_TAB_INTRO). Ranges, the
          compact counts line, the stale-data warning (2026-09-30
          follow-up: inline here now, after the counts, rather than its
          own full-width row below) shares this row. */}
      <div className="node-list-toolbar">
        <AuditWindowControls windowKey={windowKey} onWindowChange={onWindowChange} />
        {scopeOptions.length > 0 && (
          <label className="backbone-scope-picker">
            <span>Scope</span>
            <select aria-label="Backbone scope footprint" value={selectedScope} onChange={(e) => setSelectedScope(e.target.value)}>
              <option value="">All scopes</option>
              {scopeOptions.map((scope) => <option key={scope} value={scope}>{scope}</option>)}
            </select>
            <HoverTip text="Shows Backbone home-scope assignment (which can be inferred), observed relayed-scope membership, and links carrying the selected scope. Map highlighting applies only to stable links with positioned endpoints. The dotted neighbour overlay is independent. This is membership evidence, not a path or coverage calculation.">
              <span className="backbone-scope-help" tabIndex={0} aria-label="About Backbone scope footprints">ⓘ</span>
            </HoverTip>
          </label>
        )}
        {data && data !== "unavailable" && data !== "pending" && (
          <span className="settings-field-desc">
            {backboneCompactCountsLine(counts.both, counts.backhaulOnly, counts.regionalOnly, counts.other)}
          </span>
        )}
        {/* Only shown when the newest real event is older than the stale
            threshold — normally nothing renders here at all. Same amber
            "something's off" text colour as .region-filter-pending/
            .neighbor-popup-tier-oneway elsewhere in this app, not a boxed
            note. */}
        {staleWarning && <span className="backbone-stale-warning">{staleWarning}</span>}
      </div>

      {data === null && <div className="node-detail-note">{BACKBONE_LOADING}</div>}
      {data === "pending" && <div className="node-detail-note">{BACKBONE_PENDING}</div>}
      {data === "unavailable" && <div className="node-detail-note">{BACKBONE_UNAVAILABLE}</div>}
      {data && data !== "unavailable" && data !== "pending" && data.nodes.length === 0 && <div className="node-detail-note">{BACKBONE_EMPTY}</div>}

      {data && data !== "unavailable" && data !== "pending" && (
        <>
          {selectedScope && (
            <div className="backbone-scope-status" role="status">
              <strong>{selectedScope}</strong>{" · "}
              {scopeLoad === "loading" && "Loading…"}
              {scopeLoad === "error" && <>Could not load. <button type="button" className="backbone-scope-retry" onClick={() => { scopeRetryRef.current = true; setScopeRetry((n) => n + 1); }}>Retry</button></>}
              {scopeLoad === "idle" && scopeFootprint && `${scopeFootprint.repeater_count} repeaters · ${scopeFootprint.link_count} report links`}
            </div>
          )}
          <div className="backbone-map-wrap">
            <div ref={containerRef} className="backbone-map" style={{ background: MAP_CONTAINER_BACKGROUND.light }} />

            {legendOpen ? (
              <div className="backbone-legend">
                <div className="backbone-legend-head">
                  <h4 style={{ margin: 0, fontSize: 13 }}>{BACKBONE_LEGEND_PANEL_TITLE}</h4>
                  <button type="button" className="close-btn" onClick={() => setLegendOpen(false)} aria-label={BACKBONE_LEGEND_CLOSE_LABEL}>
                    &times;
                  </button>
                </div>
                <div className="legend-section">
                  <h4>{BACKBONE_LEGEND_HEADING}</h4>
                  {(["both", "backhaul", "regional_backbone", "neither"] as BackboneClass[]).map((cls) => (
                    <div className="legend-row" key={cls}>
                      <span className="legend-swatch" style={{ background: BACKBONE_CLASS_INFO[cls].color }} />
                      {backboneClassPills(cls).map((pc) => (
                        <span key={pc} className="badge" style={{ background: BACKBONE_CLASS_INFO[pc].color, color: "#17181c", marginRight: 4 }}>
                          {BACKBONE_CLASS_INFO[pc].label}
                        </span>
                      ))}
                    </div>
                  ))}
                  <label style={{ display: "block", marginTop: 6 }}>
                    <input type="checkbox" checked={showNeither} onChange={(e) => setShowNeither(e.target.checked)} /> {BACKBONE_LEGEND_TOGGLE_NEITHER}
                  </label>
                </div>

                <div className="legend-section">
                  <h4>{BACKBONE_LEGEND_LINKS_HEADING}</h4>
                  <div className="legend-row">
                    <span className="legend-swatch" style={{ background: BACKBONE_LINK_KIND_INFO.backhaul.color, borderRadius: 0, height: 3 }} />
                    {BACKBONE_LINK_KIND_INFO.backhaul.label}
                  </div>
                  <div className="legend-row">
                    <span className="legend-swatch" style={{ background: BACKBONE_LINK_KIND_INFO.backbone.color, borderRadius: 0, height: 3 }} />
                    {BACKBONE_LINK_KIND_INFO.backbone.label}
                  </div>
                  <div className="legend-row">
                    <span className="legend-swatch" style={{ background: BACKBONE_LINK_KIND_INFO.faint.color, borderRadius: 0, height: 2 }} />
                    {BACKBONE_LINK_KIND_INFO.faint.label}
                  </div>
                  <div className="settings-field-desc">{BACKBONE_LEGEND_DIRECTION_NOTE}</div>
                  <HoverTip text={TOPOLOGY_NEIGHBOUR_LINKS_TIP} placement="left" block>
                    <label className="legend-row topo-neighbour-row">
                      <input type="checkbox" checked={showNeighbour} onChange={(e) => setShowNeighbour(e.target.checked)} />
                      <span className="topo-dotted-swatch" style={{ borderTopColor: NEIGHBOUR_LINK_COLOR }} />
                      {TOPOLOGY_NEIGHBOUR_LINKS_LABEL}
                    </label>
                  </HoverTip>
                  <label style={{ display: "block", marginTop: 6 }}>
                    <input type="checkbox" checked={showFaint} onChange={(e) => setShowFaint(e.target.checked)} /> {BACKBONE_LEGEND_TOGGLE_FAINT}
                  </label>
                </div>
              </div>
            ) : (
              <button type="button" className="backbone-legend-reopen" onClick={() => setLegendOpen(true)}>
                {BACKBONE_LEGEND_PANEL_TITLE}
              </button>
            )}

            {selectedNode && (
              <>
                <button type="button" className="backbone-exit-btn" onClick={() => setSelectedId(null)}>
                  {BACKBONE_DETAILS_EXIT_LABEL}
                </button>
                <DetailsPanel node={selectedNode} windowDays={windowDays} windowKey={windowKey} neighbours={selectedNeighbours} doubtful={doubtfulIds.has(selectedNode.native_id.toLowerCase())} outsideScope={!!selectedScope && !!scopeFootprint && scopeFootprint.scope === selectedScope && !scopeNodeIds.has(selectedNode.native_id)} onExit={() => setSelectedId(null)} />
              </>
            )}
          </div>
          {data.nodes.some((n) => n.lat === null || n.lon === null) && (
            <details className="backbone-unpositioned">
              <summary>Unpositioned nodes ({data.nodes.filter((n) => n.lat === null || n.lon === null).length})</summary>
              <p className="settings-field-desc">These eligible Backbone nodes are omitted from the map because they have no known coordinates.</p>
              <label className="backbone-unpositioned-search">
                <span>Search unpositioned nodes</span>
                <input type="search" value={unpositionedQuery} onChange={(e) => setUnpositionedQuery(e.target.value)} placeholder="Name or node ID" />
              </label>
              <div className="backbone-unpositioned-list">
                {unpositionedNodes.map((node) => (
                  <div key={node.native_id} className="backbone-unpositioned-row">
                    <div className="backbone-unpositioned-identity">
                      <a href={`#/nodes/${encodeURIComponent(node.native_id)}`}>{node.name || node.short_id}</a>
                      <span>{node.short_id}</span>
                      {scopeLoad === "idle" && scopeFootprint?.scope === selectedScope && selectedScope && (
                        <span className={`backbone-scope-membership ${scopeNodeIds.has(node.native_id) ? "is-member" : "is-outside"}`}>
                          {scopeNodeIds.has(node.native_id) ? "In selected scope" : "Outside selected scope"}
                        </span>
                      )}
                    </div>
                    <button type="button" className="rf-btn rf-btn-outline" onClick={() => {
                      setSelectedId(node.native_id);
                      // The list sits after the map/details panel in document order. Move keyboard
                      // focus and the viewport to the newly opened panel, including repeat clicks.
                      requestAnimationFrame(() => requestAnimationFrame(() => {
                        const panel = document.querySelector<HTMLElement>(".backbone-details");
                        panel?.scrollIntoView({ block: "start", behavior: "auto" });
                        panel?.querySelector<HTMLElement>("h3")?.focus({ preventScroll: true });
                      }));
                    }} aria-label={`Open Backbone details for ${node.name || node.short_id}`}>Backbone details</button>
                  </div>
                ))}
                {unpositionedNodes.length === 0 && <div className="settings-field-desc">No matching unpositioned nodes.</div>}
              </div>
            </details>
          )}
        </>
      )}
    </section>
  );
}
